
import React from "react";
import { Search, Edit, Trash2 } from "lucide-react";
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RecipeIdea } from "../types";

interface RecipeListProps {
  recipes: RecipeIdea[];
  isLoading: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onEdit: (recipe: RecipeIdea) => void;
  onDelete: (id: string) => void;
}

const RecipeList: React.FC<RecipeListProps> = ({
  recipes,
  isLoading,
  searchTerm,
  onSearchChange,
  onEdit,
  onDelete
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recipe Ideas</CardTitle>
        <CardDescription>
          Manage your holiday recipe collection
        </CardDescription>
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search recipes..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center p-4">Loading recipes...</div>
        ) : recipes.length === 0 ? (
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No recipes found.</p>
            {searchTerm && (
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search terms.</p>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {recipes.map((recipe) => (
              <div
                key={recipe.id}
                className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex justify-between">
                  <div>
                    <h3 className="text-lg font-medium">{recipe.title}</h3>
                    <p className="text-gray-500 text-sm">{recipe.description.substring(0, 120)}...</p>
                    <div className="flex mt-2 gap-2 flex-wrap">
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {recipe.category}
                      </span>
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {recipe.difficulty}
                      </span>
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {recipe.totalTime}
                      </span>
                      {recipe.kidFriendly && (
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                          Kid Friendly
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(recipe)}
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(recipe.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecipeList;
